/*
 * **Die Checkliste als Baum** (#181, #211): Die Zeilen kommen in Vorordnung, jede mit ihrer Ebene
 * und ihrem Eltern-Teil. Hier steht die Rechnung, die die Liste daraus braucht — wo Linien laufen,
 * welche Zeilen einen Ast mitnehmen, wer Geschwister ist. Reine Funktionen, ohne React.
 */

export interface TreeRow {
  id: string
  /** 1 = direkt unter der Aufgabe. Ohne Angabe: 1 (ein Entwurf ist flach). */
  depth?: number
  parentId?: string
}

/**
 * Was eine Zeile an Linien zeichnet (Prototyp-Variante E, wie Linear). `level` zählt ab 0 (Ebene 1).
 *
 * - `elbow`: der Bogen vom Stamm des Eltern-Teils an den eigenen Haken — ab Ebene 2.
 * - `continues`: unter dem Bogen läuft der Stamm weiter, weil noch ein Geschwister folgt.
 * - `rails`: Ebenen weiter links, auf denen ein Strich durch die Zeile läuft (dort folgt noch ein
 *   Geschwister eines Vorfahren).
 * - `stem`: Unter dem eigenen Haken beginnt ein Stamm — die Zeile hat Kinder.
 */
export interface TreeLines {
  level: number
  elbow: boolean
  continues: boolean
  rails: number[]
  stem: boolean
}

const depthOf = (row: TreeRow) => row.depth ?? 1

/** Die Linien jeder Zeile, nach Kennung. `rows` in Vorordnung, wie die Liste sie zeigt. */
export function treeLines(rows: readonly TreeRow[]): Map<string, TreeLines> {
  // Folgt auf derselben Ebene noch ein Geschwister, bevor der Ast endet?
  const hasNextSibling = rows.map((row, i) => {
    const d = depthOf(row)
    for (let j = i + 1; j < rows.length; j++) {
      const other = depthOf(rows[j]!)
      if (other < d) return false
      if (other === d) return true
    }
    return false
  })
  const lines = new Map<string, TreeLines>()
  // Je Ebene: ob der Vorfahr dort noch ein Geschwister nach sich hat.
  const open: boolean[] = []
  rows.forEach((row, i) => {
    const level = depthOf(row) - 1
    open.length = level
    const rails: number[] = []
    // Ein Vorfahr auf Ebene k (ab 1) mit folgendem Geschwister zieht seinen Stamm (bei k − 1) durch.
    for (let k = 1; k < level; k++) if (open[k]) rails.push(k - 1)
    lines.set(row.id, {
      level,
      elbow: level >= 1,
      continues: level >= 1 && hasNextSibling[i]!,
      rails,
      stem: i + 1 < rows.length && depthOf(rows[i + 1]!) > depthOf(row),
    })
    open[level] = hasNextSibling[i]!
  })
  return lines
}

/**
 * Die angefassten Zeilen, die selbst handeln: ohne die, deren Vorfahr auch angefasst ist — die
 * gehen in dessen Ast mit (Löschen, Herauslösen, Abhaken, Duplizieren, Ziehen). In Listenreihenfolge.
 */
export function branchHeads<T extends TreeRow>(rows: readonly T[], ids: Iterable<string>): T[] {
  const picked = new Set(ids)
  const byId = new Map(rows.map(row => [row.id, row]))
  return rows.filter(row => {
    if (!picked.has(row.id)) return false
    for (let parent = row.parentId ? byId.get(row.parentId) : undefined; parent; parent = parent.parentId ? byId.get(parent.parentId) : undefined) {
      if (picked.has(parent.id)) return false
    }
    return true
  })
}

/** Die Zeilen unter `id` in Vorordnung — die folgenden mit größerer Ebene. */
export function descendantRows<T extends TreeRow>(rows: readonly T[], id: string): T[] {
  const index = rows.findIndex(row => row.id === id)
  if (index === -1) return []
  const d = depthOf(rows[index]!)
  const out: T[] = []
  for (let j = index + 1; j < rows.length && depthOf(rows[j]!) > d; j++) out.push(rows[j]!)
  return out
}

/** Die letzte Zeile des Astes von `id` (sie selbst, wenn sie keine Kinder hat). */
export function lastOfBranch<T extends TreeRow>(rows: readonly T[], id: string): T | undefined {
  return descendantRows(rows, id).at(-1) ?? rows.find(row => row.id === id)
}

/** Die Geschwister von `id` (derselbe Eltern-Teil), in Listenreihenfolge, sie selbst eingeschlossen. */
export function siblingRows<T extends TreeRow>(rows: readonly T[], id: string): T[] {
  const row = rows.find(r => r.id === id)
  if (!row) return []
  return rows.filter(r => r.parentId === row.parentId && depthOf(r) === depthOf(row))
}
